import React, { Fragment, useCallback } from "react";
import {
    Checkbox,
    FormControlLabel,
    FormLabel,
    Grid,
    Radio,
    RadioGroup,
    Typography,
    FormControl,
    Select,
    MenuItem,
    Chip,
    Box,
    FormHelperText,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { IStep } from "../DigitalSign";
import { ChannelFieldName, COMMUNICATION_CHANNELS, ConsentKey, IOptions } from "../../types";

export interface IConsentStep {
    step: IStep & { options: IOptions };
    setStep?: (step: IStep) => void;
}

interface ConsentConfig {
    key: ConsentKey;
    type: "radio" | "checkbox";
    translationKey: string;
    hasChannelSelection: boolean;
    showChannelsFor: readonly string[];
}

// Mapping Consent → Channels
const consentToChannelMap: Record<ConsentKey, ChannelFieldName> = {
    promoDrivaliaConsent: "promoDrivaliaConsentChannels",
    preferenciesConsent: "preferenciesConsentChannels",
    promoOthersConsent: "promoOthersConsentChannels",
    viewInfoAck: "viewInfoAckChannels",
};

const tenantsForChannel = ["drivalia_fr"]

// Configurazioni dei consensi
const CONSENT_CONFIGS: ConsentConfig[] = [
    {
        key: "viewInfoAck",
        type: "radio",
        translationKey: "viewInfoAck",
        hasChannelSelection: false,
        showChannelsFor: tenantsForChannel,
    },
    {
        key: "promoOthersConsent",
        type: "checkbox",
        translationKey: "promoOthersConsent",
        hasChannelSelection: true,
        showChannelsFor: tenantsForChannel,
    },
    {
        key: "preferenciesConsent",
        type: "checkbox",
        translationKey: "preferenciesConsent",
        hasChannelSelection: true,
        showChannelsFor: tenantsForChannel,
    },
    {
        key: "promoDrivaliaConsent",
        type: "checkbox",
        translationKey: "promoDrivaliaConsent",
        hasChannelSelection: true,
        showChannelsFor: tenantsForChannel,
    },
];

const ConsentStep = ({ step, setStep }: IConsentStep) => {
    const { t } = useTranslation(["network"]);
    const tenant = step.tenant || "drivalia_it";

    const updateStepOptions = useCallback(
        (updates: Partial<IOptions>) => {
            if (!setStep) return;
            setStep({
                ...step,
                options: {
                    ...step.options,
                    ...updates,
                },
            });
        },
        [step, setStep]
    );

    const handleCheckboxChange = useCallback(
        (key: ConsentKey, checked: boolean) => {
            const updates: Partial<IOptions> = {
                [key]: checked,
            };

            const channelKey = consentToChannelMap[key];
            if (!checked) {
                updates[channelKey] = [];
            } else {
                if (!tenantsForChannel.includes(tenant)) {
                    updates[channelKey] = ['Others'];
                }
            }

            updateStepOptions(updates);
        },
        [updateStepOptions]
    );

    const handleRadioChange = useCallback(
        (key: ConsentKey, value: boolean) => {
            const updates: Partial<IOptions> = {
                [key]: value,
            };

            if (!value) {
                const channelKey = consentToChannelMap[key];
                if (tenantsForChannel.includes(tenant)) {
                    updates[channelKey] = [];
                } else {
                    updates[channelKey] = ['Others'];
                }

            }

            updateStepOptions(updates);
        },
        [updateStepOptions]
    );

    const handleChannelChange = useCallback(
        (key: ConsentKey) => (event: any) => {
            const value = event.target.value;
            const channelKey = consentToChannelMap[key];

            updateStepOptions({
                [channelKey]: typeof value === "string" ? value.split(",") : value,
            });
        },
        [updateStepOptions]
    );

    const renderChannelSelection = useCallback(
        (key: ConsentKey, consentValue?: boolean | null, translationKey?: string) => {
            if (!consentValue) return null;

            const channelKey = consentToChannelMap[key];
            const selectedChannels: string[] = step.options[channelKey] ?? [];
            const hasError = selectedChannels.length === 0;

            return (
                <Grid item xs={12} sx={{ mt: 1, ml: 3 }} key={`${key}-channels`}>
                    <FormControl fullWidth size="small" error={hasError}>
                        <FormLabel sx={{ mb: 1, fontSize: "0.875rem" }}>
                            {t(`digitalSign.consent.${translationKey}Channels`)}
                        </FormLabel>
                        <Select
                            multiple
                            value={selectedChannels}
                            onChange={handleChannelChange(key)}
                            renderValue={(selected) => (
                                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                    {(selected as string[]).map((value) => {
                                        const channel = COMMUNICATION_CHANNELS.find((ch) => ch.value === value);
                                        return <Chip key={value} label={channel?.label || value} size="small" />;
                                    })}
                                </Box>
                            )}
                        >
                            {COMMUNICATION_CHANNELS.map((channel) => (
                                <MenuItem key={channel.value} value={channel.value}>
                                    {channel.label}
                                </MenuItem>
                            ))}
                        </Select>
                        {hasError && (
                            <FormHelperText>{t("digitalSign.consent.validation.selectAtLeastOne")}</FormHelperText>
                        )}
                    </FormControl>
                </Grid>
            );
        },
        [step.options, handleChannelChange, t]
    );

    const renderCheckboxConsent = useCallback(
        (key: ConsentKey, translationKey: string, consentValue?: boolean | null) => (
            <Grid item xs={12} sx={{ mt: 2 }} key={key}>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={!!consentValue}
                            onChange={(e) => handleCheckboxChange(key, e.target.checked)}
                        />
                    }
                    label={t(`digitalSign.consent.${translationKey}`)}
                />
            </Grid>
        ),
        [handleCheckboxChange, t]
    );

    const renderRadioConsent = useCallback(
        (key: ConsentKey, translationKey: string, consentValue?: boolean | null) => (
            <Grid item xs={12} key={key}>
                <FormLabel id={`ntw_consent_${key}`}>{t(`digitalSign.consent.${translationKey}`)}</FormLabel>
                <RadioGroup
                    row
                    aria-labelledby={`ntw_consent_${key}`}
                    value={consentValue === true ? "true" : consentValue === false ? "false" : ""}
                    onChange={(e) => handleRadioChange(key, e.target.value === "true")}
                >
                    <FormControlLabel value="true" control={<Radio />} label={t("digitalSign.consent.option.yes")} />
                    <FormControlLabel value="false" control={<Radio />} label={t("digitalSign.consent.option.no")} />
                </RadioGroup>
            </Grid>
        ),
        [handleRadioChange, t]
    );

    const shouldShowChannelSelection = useCallback(
        (config: ConsentConfig) => config.hasChannelSelection && config.showChannelsFor.includes(tenant),
        [tenant]
    );

    return (
        <Grid container sx={{ width: "80%", marginTop: "16px" }}>
            <Grid item xs={12} mb={2}>
                <Typography variant="h6">{t("digitalSign.consent.hint")}</Typography>
            </Grid>

            {CONSENT_CONFIGS.map((config) => (
                <Fragment key={config.key}>
                    {config.type === "radio"
                        ? renderRadioConsent(config.key, config.translationKey, step.options[config.key])
                        : renderCheckboxConsent(config.key, config.translationKey, step.options[config.key])}

                    {shouldShowChannelSelection(config) &&
                        renderChannelSelection(config.key, step.options[config.key], config.translationKey)}
                </Fragment>
            ))}
        </Grid>
    );
};

export default ConsentStep;
